<template>
	<div v-if="trip" class="d-flex height-full-dynamic-viewport" :class="isXS ? 'flex-column' : ''">
		<TripMenu
			v-if="!isXS"
			:trip="trip"
			:destination="trip.destination!"
			:secondary-menu-items="secondaryMenuItems"
			@open-invitation-dialog="openInvitationDialog"
			@open-plan-trip="openPlanTripDialog"
			@open-request-help="openRequestHelpDialog"
			@change-dates="changeDates"
		/>
		<div v-else class="bg-primary d-flex align-center justify-space-between pa-4 relative z-7">
			<PButton size="sm" type="text" color="neutral" @click="goBack">
				<Icon
					:icon="isDashboard ? 'close' : 'chevron'"
					:radius="isDashboard ? '20px' : '16px'"
					rotate="right"
				/>
			</PButton>
			<div class="d-flex gap-2">
				<h2 class="text-title-m">{{ $t(getRouteTitle()) }}</h2>
				<NewIcon
					v-if="getRouteLegalInfo()"
					class="align-self-center text-neutral-700"
					icon="info"
					@click="openLegalInfo"
				/>
			</div>

			<Dropdown>
				<template #trigger="{ toggle }">
					<PButton only-icon icon="more" size="sm" type="text" color="neutral" @click="toggle" />
				</template>
				<template #default="{ close }">
					<div class="pa-4">
						<TripMenuList
							:secondary-menu-items="secondaryMenuItems"
							:is-x-s="isXS"
							:enable-request-help-option="enableRequestHelpOption"
							:has-products="hasProducts"
							:has-activities="hasActivities"
							@close="close"
							@open-invitation-dialog="openInvitationDialog"
							@open-plan-trip="openPlanTripDialog"
							@open-request-help="openRequestHelpDialog"
							@exit="goHome"
						/>
					</div>
				</template>
			</Dropdown>
		</div>
		<div class="flex-grow-1" :style="{ overflowY: isXS ? 'auto' : 'hidden' }">
			<slot />
		</div>
	</div>
</template>

<script setup lang="ts">
	import { storeToRefs } from 'pinia'
	import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
	import TripActions from '~/actions/trip/action'
	import { TripTravelerActions } from '~/actions/trip/traveler'
	import {
		DialogBuilder,
		DialogController,
	} from '~/components/passporterUi/Dialog/dialogController'
	import { useTrip } from '~/composables/trip'
	import { HomeNavigation } from '~/services/navigation/home'
	import { TripNavigation } from '~/services/navigation/trip'
	import { useDeviceStore } from '~/store/device'
	import TripRequestHelpDialog from '~/components/trip/tripRequestHelp/TripRequestHelpDialog.vue'
	import { useProfileStore } from '~/store/profile'
	import { ItineraryActions } from '~/actions/trip/itinerary'
	import { MenuItem } from '~/composables/tripMenu'

	const deviceStore = useDeviceStore()
	const { isXS } = storeToRefs(deviceStore)
	const profileStore = useProfileStore()
	const { profile } = storeToRefs(profileStore)
	const route = useRoute()

	const tripId: string = route.params.itinerary_id as string
	const enableRequestHelpOption = ref<boolean>(false)
	const { t } = useI18n()

	const { trip, hasProducts, hasActivities, getTrip, getProducts, getActivities, resetTrip } =
		useTrip(tripId)

	const requestHelpDialog = createRequestHelpDialog()

	onMounted(async () => {
		getTrip()
		getProducts()
		getActivities()
	})

	onBeforeUnmount(() => resetTrip())

	watch(
		() => trip.value?.destination,
		() => displayRequestHelpOption(),
		{
			deep: true,
		}
	)

	const isDashboard = computed(() => {
		const currentRoute = route.name as string
		return currentRoute.includes('dashboard')
	})

	const getRouteTitle = () => {
		const currentRoute = route.name as string
		if (!currentRoute) return ''

		const titles = {
			'store-ideas': 'web.trip.sidebar.ideas',
			'store-activities': 'web.trip.sidebar.activities',
			'dashboard': 'web.trip.sidebar.summary',
			'spots': 'web.trip.sidebar.places',
			'tasks': 'web.trip.sidebar.tasks',
			'documents': 'web.trip.sidebar.bookings',
			'wall': 'web.board.header.title',
			'indispensables': 'web.essential.header.title',
			'itinerary': 'web.trip.sidebar.itinerary',
		}
		const found = Object.entries(titles).find(([key]) => currentRoute.includes(key))
		return found?.[1] || ''
	}

	const getRouteLegalInfo = (): Record<string, string> | undefined => {
		const currentRoute = route.name as string
		if (!currentRoute) return
		const titles = {
			indispensables: {
				title: t('web.essential.header.tooltip_title'),
				text: t('web.essential.header.tooltip'),
			},
		}
		const found = Object.entries(titles).find(([key]) => currentRoute.includes(key))
		return found?.[1]
	}

	const openLegalInfo = () => {
		const legalInfo = getRouteLegalInfo()
		const setup = new DialogBuilder()
			.title(legalInfo?.title || '')
			.bodyText(legalInfo?.text || '')
			.size('s')
			.mobileType('bottomSheet')
			.build()
		new DialogController(setup).open()
	}

	const displayRequestHelpOption = async () => {
		const destination = trip.value?.destination
		if (!destination) return
		enableRequestHelpOption.value = destination.isMonetizable
	}

	const openInvitationDialog = async () => {
		const invitationDialog: DialogController = await TripTravelerActions.createInvitationDialog(
			trip.value!,
			isXS.value
		)
		invitationDialog.open()
	}

	const openRequestHelpDialog = () => {
		requestHelpDialog.open({ isXS: isXS.value, trip, profile: profile.value, utmCampaign: 'menu' })
	}

	function openPlanTripDialog() {
		TripActions.createTripDialog().open({
			trip: trip.value,
			isEdit: true,
		})
	}

	function createRequestHelpDialog() {
		const setup = new DialogBuilder(TripRequestHelpDialog)
			.size('s')
			.mobileType('fullScreen')
			.borderless()
			.build()
		return new DialogController(setup)
	}

	const changeDates = async (dates?: Date[]): Promise<void> => {
		if (!dates || dates.length < 2) return
		trip.value = await ItineraryActions.editDates(tripId, dates)
	}

	const secondaryMenuItems = computed(() => {
		const items: MenuItem[] = [
			{
				name: 'web.trip.sidebar.invite',
				icon: 'user-plus',
				action: () => openInvitationDialog(),
			},
			{
				name: 'web.trip.sidebar.settings',
				icon: 'gear',
				action: () => openPlanTripDialog(),
			},
			{
				name: 'web.trip.sidebar.contact',
				icon: 'question',
				action: () => openRequestHelpDialog(),
				hide: !enableRequestHelpOption.value,
			},
		]
		return items
	})

	const goHome = () => {
		HomeNavigation.toHome()
	}

	const goBack = () => {
		if (isDashboard.value) return HomeNavigation.toHome()
		TripNavigation.toTripDashboard(tripId)
	}
</script>
